import type { AppLocale } from '../i18n';
import type { LessonExerciseSummaryDto, LessonQuizDto } from '../services/contentService';
import type { LessonJourneyStep } from '../components/learning/LessonJourneyTimeline';
import { pickLocalizedTitle } from './localizedContent';
import { isDesignedForNonFarsiUsers } from './nonFarsiContentHint';
import type { JourneyTranslate } from './lessonJourneySteps';

function itemTitle(
  item: { title?: string | null; titleFa?: string | null; isDesignedForNonFarsiUsers?: boolean },
  locale: AppLocale
): string {
  const contentLocale = isDesignedForNonFarsiUsers(item) ? 'en' : locale;
  return pickLocalizedTitle(item, contentLocale);
}

export function tagItemViewStorageKey(tagId: number, scope: string): string {
  return `tag-item-viewed:${tagId}:${scope}`;
}

/** Explicit mark wins. Otherwise a tag item that is already passed counts as viewed. */
export function readTagItemViewed(tagId: number, scope: string, passed: boolean): boolean {
  try {
    const raw = localStorage.getItem(tagItemViewStorageKey(tagId, scope));
    if (raw === '1') return true;
    if (raw === '0') return false;
  } catch {
    /* private mode */
  }
  return passed;
}

export function writeTagItemViewed(tagId: number, scope: string, viewed: boolean): void {
  try {
    localStorage.setItem(tagItemViewStorageKey(tagId, scope), viewed ? '1' : '0');
  } catch {
    /* private mode */
  }
}

export function tagJourneyGatesClear(
  exercises: LessonExerciseSummaryDto[],
  quizzes: LessonQuizDto[]
): boolean {
  return (
    exercises.every((exercise) => exercise.userHasCompletedExercise === true) &&
    quizzes.every((quiz) => quiz.userHasPassedQuiz === true)
  );
}

/**
 * Journey for one tag lesson: view the tags, then any mandatory exercise or quiz that is part of this tag.
 * Viewing alone passes the tag lesson when it has no mandatory exercise or quiz.
 */
export function buildTagJourneySteps(options: {
  viewed: boolean;
  passed: boolean;
  markingView?: boolean;
  exercises: LessonExerciseSummaryDto[];
  quizzes: LessonQuizDto[];
  onMarkViewed: () => void;
  onUnmarkViewed: () => void;
  onOpenExercise: (exerciseId: number) => void;
  onOpenQuiz: (quizId: number) => void;
  jt?: JourneyTranslate;
  locale?: AppLocale;
}): LessonJourneyStep[] {
  const jt = options.jt;
  const locale = options.locale ?? 'en';
  const gatesClear = tagJourneyGatesClear(options.exercises, options.quizzes);
  const passed = options.passed && options.viewed && gatesClear;

  const steps: LessonJourneyStep[] = [
    {
      id: 'review',
      label: jt?.('journey.viewTags') ?? 'View the tags',
      hint: jt?.('journey.viewTagsHint') ?? 'Mark the tagged content as viewed',
      status: options.viewed ? 'complete' : 'current',
      actionLabel: options.viewed
        ? (jt?.('journey.markNotViewed') ?? 'Mark as not viewed')
        : (jt?.('journey.markViewed') ?? 'Mark as viewed'),
      onAction: options.viewed ? options.onUnmarkViewed : options.onMarkViewed,
      actionDisabled: options.markingView,
      actionLoading: options.markingView
    }
  ];

  for (const exercise of options.exercises) {
    const done = exercise.userHasCompletedExercise === true;
    steps.push({
      id: `exercise-${exercise.id}`,
      label: itemTitle(exercise, locale),
      hint: jt?.('journey.answerCorrectly') ?? 'Answer every question correctly',
      status: done ? 'complete' : 'upcoming',
      actionLabel: done
        ? (jt?.('exercise.practiceAgain') ?? 'Practice again')
        : (jt?.('journey.openExercise') ?? 'Open exercise'),
      onAction: () => options.onOpenExercise(exercise.id)
    });
  }

  for (const quiz of options.quizzes) {
    const done = quiz.userHasPassedQuiz === true;
    steps.push({
      id: `quiz-${quiz.id}`,
      label: itemTitle(quiz, locale),
      hint: jt?.('journey.passWithScore', { score: quiz.passingScore }) ?? `Pass with at least ${quiz.passingScore}%`,
      status: done ? 'complete' : 'upcoming',
      actionLabel: done ? undefined : (jt?.('journey.takeQuiz') ?? 'Take quiz'),
      onAction: done ? undefined : () => options.onOpenQuiz(quiz.id)
    });
  }

  steps.push({
    id: 'mark-passed',
    label: jt?.('tagLesson.passStep.label') ?? 'Passed',
    hint: options.viewed && gatesClear
      ? (jt?.('tagLesson.passStep.hintWhenReady') ?? 'This tag lesson is passed')
      : (jt?.('journey.completeStepsFirst') ?? 'View the tags and pass any mandatory exercise or quiz'),
    status: passed ? 'complete' : options.viewed && gatesClear ? 'current' : 'upcoming'
  });

  return steps;
}
